import type { ReactElement } from 'react';
import type { ExtensionVersion } from '@kirin/api-client/contracts/extensions';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { VersionUsersCell } from './CellRenderers';
import { SecuritySummaryCell } from '@/components/shared/extensions/SecuritySummaryCell';
import { RiskEstimateCell } from '@/components/shared/extensions/RiskEstimateCell';

interface ExtensionVersionsTableProps {
  versions: ExtensionVersion[];
  /** KIR-5281: called with the clicked version row. */
  onUserClick?: (version: string) => void;
  organizationId: string;
  extensionId: string;
  capabilityDetectionEnabled: boolean;
  /** KIR-1928: adds a per-version "View in AgentMesh" console link column. */
  agentmeshRiskEnabled?: boolean;
  /** KIR-4726: built-ins render "Not scanned — built-in" per version. */
  isBuiltIn?: boolean;
}

/**
 * Table showing extension version details in the expanded row.
 * Matches legacy ExtensionsTab implementation exactly.
 * Displays version string, clickable user count, and first seen date.
 */
export function ExtensionVersionsTable({
  versions,
  onUserClick,
  organizationId,
  extensionId,
  capabilityDetectionEnabled,
  agentmeshRiskEnabled = false,
  isBuiltIn = false,
}: ExtensionVersionsTableProps): ReactElement {
  if (versions.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        No version information available
      </div>
    );
  }

  return (
    <div className="pl-8 py-4">
      <div className="text-sm font-medium mb-2">Versions:</div>
      <Table className="text-xs">
        <TableHeader className="border-b bg-muted">
          <TableRow className="!bg-transparent hover:!bg-transparent">
            <TableHead>Version</TableHead>
            <TableHead className="text-center">Users</TableHead>
            <TableHead>First Seen</TableHead>
            {capabilityDetectionEnabled && <TableHead>Risk</TableHead>}
            {capabilityDetectionEnabled && <TableHead />}
            {agentmeshRiskEnabled && <TableHead>AgentMesh</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map(version => (
            <TableRow
              key={version.version}
              className="border-b hover:bg-muted/50"
            >
              <TableCell>{version.version}</TableCell>
              <TableCell className="text-center">
                <VersionUsersCell
                  userCount={version.userCount}
                  onClick={
                    onUserClick ? () => onUserClick(version.version) : undefined
                  }
                />
              </TableCell>
              <TableCell className="text-muted-foreground">
                <RelativeTimeWithTooltip date={version.firstSeenAt} />
              </TableCell>
              {capabilityDetectionEnabled && (
                <TableCell>
                  <SecuritySummaryCell summary={version.securitySummary} />
                </TableCell>
              )}
              {capabilityDetectionEnabled && (
                <TableCell>
                  {version.securitySummary != null ? (
                    <Link
                      to={`/${organizationId}/inventory/extensions/${encodeURIComponent(extensionId)}/scan-result?version=${encodeURIComponent(version.version)}`}
                      className="text-primary hover:underline"
                    >
                      View scan result →
                    </Link>
                  ) : null}
                </TableCell>
              )}
              {agentmeshRiskEnabled && (
                <TableCell>
                  <span className="inline-flex items-center gap-2">
                    <RiskEstimateCell
                      riskEstimate={version.riskEstimate}
                      // Per version: a marketplace version of a built-in is
                      // scanned, so without a result it is "Not assessed".
                      isBuiltIn={version.isBuiltIn ?? isBuiltIn}
                    />
                    {version.agentmeshUrl ? (
                      <a
                        href={version.agentmeshUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whitespace-nowrap text-primary hover:underline"
                      >
                        View in AgentMesh ↗
                      </a>
                    ) : null}
                  </span>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

ExtensionVersionsTable.displayName = 'ExtensionVersionsTable';
